import { useState } from 'react'
import type { CoupleCertification, CertificationPerson } from '@/utils/api'

type FilterType = 'All' | 'Strength' | 'Weakness'

interface CertificationApprovalTabProps {
  data?: CoupleCertification | null
}

// Certificate preview placeholder
const CertificatePreview = () => (
  <div className='relative w-full h-[375px] mb-6'>
    <div
      className='absolute inset-0'
      style={{
        background: 'linear-gradient(to bottom, rgba(255,255,255,0) 45%, rgba(255,255,255,1) 87%)',
      }}
    />
  </div>
)

// Not eligible warning icon
const NotEligibleIcon = () => (
  <div className='w-[113px] h-[77px] flex items-center justify-center'>
    <svg width='64' height='64' viewBox='0 0 64 64' fill='none'>
      <circle cx='32' cy='32' r='30' stroke='#f04438' strokeWidth='3' />
      <line x1='18' y1='18' x2='46' y2='46' stroke='#f04438' strokeWidth='3' />
      <rect x='20' y='24' width='24' height='16' rx='2' stroke='#f04438' strokeWidth='2' />
    </svg>
  </div>
)

const FilterButtons = ({ selected, onChange }: { selected: FilterType; onChange: (filter: FilterType) => void }) => (
  <div className='flex gap-[7px]'>
    {(['All', 'Strength', 'Weakness'] as FilterType[]).map((filter) => (
      <button
        key={filter}
        onClick={() => onChange(filter)}
        className={`px-4 py-1 rounded-[17px] text-[12px] font-normal transition-colors ${
          selected === filter
            ? 'bg-[#FF6600] text-white'
            : 'bg-[rgba(235,235,235,0.88)] text-[#434343] border border-dashed border-[rgba(108,108,108,0.6)]'
        }`}
      >
        {filter}
      </button>
    ))}
  </div>
)

const PersonCertification = ({
  title,
  person,
  filter,
  onFilterChange,
}: {
  title: string
  person?: CertificationPerson | null
  filter: FilterType
  onFilterChange: (filter: FilterType) => void
}) => {
  const eligible = !!person?.eligible

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-[18px] font-bold text-[#151515] leading-[28px]'>{title}</h2>
        <FilterButtons selected={filter} onChange={onFilterChange} />
      </div>
      <div className='bg-white border border-[#e8e8e8] rounded-[10px] h-[643px] relative'>
        {eligible ? (
          <div className='p-[55px]'>
            <CertificatePreview />
            <div className='flex justify-center gap-3'>
              <button className='px-4 py-2 border border-[#FF6600] rounded-[8px] text-[14px] font-bold text-[#151515]'>
                View Certificate
              </button>
              <button className='px-4 py-2 bg-[#FF6600] border border-[#e0e0e0] rounded-[8px] text-[14px] font-bold text-white w-[143px]'>
                Approve
              </button>
            </div>
          </div>
        ) : (
          <div className='absolute inset-0 flex items-center justify-center'>
            <div className='flex flex-col items-center gap-4'>
              <NotEligibleIcon />
              <p className='text-[14px] text-[#535862] font-normal'>
                User not eligible to be certified yet
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function CertificationApprovalTab({ data }: CertificationApprovalTabProps) {
  const [manFilter, setManFilter] = useState<FilterType>('All')
  const [womanFilter, setWomanFilter] = useState<FilterType>('All')

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
      <PersonCertification
        title='Man'
        person={data?.personOne ?? null}
        filter={manFilter}
        onFilterChange={setManFilter}
      />
      <PersonCertification
        title='Woman'
        person={data?.personTwo ?? null}
        filter={womanFilter}
        onFilterChange={setWomanFilter}
      />
    </div>
  )
}
